export interface PricingSelection {
  plan: 'starter' | 'pro' | 'agency';
  interval: 'monthly' | 'yearly';
  currency: 'usd' | 'eur' | 'czk';
}

const STORAGE_KEY = 'pricing_selection_v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// A choice from the public site is only a preselection. Prices and permissions
// still come from the billing API; no arbitrary redirect or price is accepted.
export function parsePricingSelection(search: string): PricingSelection | null {
  const params = new URLSearchParams(search);
  if (['plan', 'interval', 'currency'].some(key => params.getAll(key).length > 1)) return null;
  const plan = params.get('plan');
  const interval = params.get('interval') ?? 'monthly';
  const currency = params.get('currency') ?? 'usd';
  if (plan !== 'starter' && plan !== 'pro' && plan !== 'agency') return null;
  if (interval !== 'monthly' && interval !== 'yearly') return null;
  if (currency !== 'usd' && currency !== 'eur' && currency !== 'czk') return null;
  return { plan, interval, currency };
}

export function pricingUpgradePath(selection: PricingSelection): string {
  return `/admin/billing/upgrade?${new URLSearchParams({ ...selection })}`;
}

export function rememberPricingSelection(selection: PricingSelection): void {
  try {
    // localStorage also survives email verification opened in a new tab.
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...selection, savedAt: Date.now() }));
  } catch {
    // Router state still carries the destination if storage is blocked.
  }
}

export function clearPricingSelection(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be blocked. */ }
}

export function getPendingPricingSelection(): PricingSelection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const item = JSON.parse(raw);
    if (!item || typeof item.savedAt !== 'number' || !Number.isFinite(item.savedAt)
        || item.savedAt > Date.now() || Date.now() - item.savedAt > MAX_AGE_MS) {
      clearPricingSelection();
      return null;
    }
    const selection = parsePricingSelection(new URLSearchParams({
      plan: item.plan, interval: item.interval, currency: item.currency,
    }).toString());
    if (!selection) clearPricingSelection();
    return selection;
  } catch {
    clearPricingSelection();
    return null;
  }
}
